/**
 * 显示模式（DisplayMode）纯函数工具。
 *
 * 集中定义模式的权威类型、URL/存储别名表与解析优先级：
 * 显式 URL ?mode= 参数 > 嵌入壳激活（embed=1 / Tauri）> 本地存储偏好 > 默认 full。
 *
 * 本模块不依赖 Vue / Pinia / 路由，也不执行任何导航（跨工作区跳转由路由层负责）；
 * 存储读写全部经 try/catch 保护：隐私模式下读取失败回退默认值、写入失败静默忽略。
 */

/** 显示模式：chat 主界面 / 灵枢工作台 / 诊断工作台 / 医嘱工作台 / 插件紧凑 / HIS 嵌入 / 教学自学 */
export type DisplayMode =
  | "full"
  | "workspace"
  | "diagnosis"
  | "order"
  | "plugin"
  | "embed"
  | "teach";

/** 可持久化的模式：embed 由宿主 URL/Tauri 临时激活，不写入存储 */
export type PersistableDisplayMode = Exclude<DisplayMode, "embed">;

/** 模式偏好的 localStorage 键（历史沿用，不可更改） */
export const DISPLAY_MODE_STORAGE_KEY = "pgy.displayMode";

/**
 * URL ?mode= 与存储值共用的别名表（小写键）。
 * 旧产品别名（银杏叶/仙鹤草）与旧紧凑别名保留兼容，禁止移除。
 */
const DISPLAY_MODE_ALIASES: Readonly<Record<string, DisplayMode>> =
  Object.freeze({
    full: "full",
    chat: "full",
    xianhecao: "full",
    workspace: "workspace",
    work: "workspace",
    yinxingye: "workspace",
    diagnosis: "diagnosis",
    order: "order",
    plugin: "plugin",
    compact: "plugin",
    embed: "embed",
    teach: "teach",
  });

/** 存储读写最小接口（便于测试注入替身） */
export interface DisplayModeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function normalizeKey(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase();
  return key ? key : null;
}

/** 规范化 URL ?mode= 参数；非法或空值返回 null */
export function normalizeDisplayModeParam(raw: unknown): DisplayMode | null {
  const key = normalizeKey(raw);
  return key ? (DISPLAY_MODE_ALIASES[key] ?? null) : null;
}

/** 规范化存储原始值：embed 不持久化、非法值返回 null，旧别名按别名表归一 */
export function normalizeStoredDisplayMode(
  raw: unknown,
): PersistableDisplayMode | null {
  const mode = normalizeDisplayModeParam(raw);
  if (!mode || mode === "embed") return null;
  return mode;
}

function defaultStorage(): DisplayModeStorage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage ?? null;
  } catch {
    // 隐私模式 / 被策略禁用时，访问 window.localStorage 本身即可抛 SecurityError
    return null;
  }
}

/** 读取持久化偏好；不可用、非法或 embed 值均返回 null（调用方回退默认） */
export function readPersistedDisplayMode(
  storage?: DisplayModeStorage | null,
): PersistableDisplayMode | null {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return null;
  try {
    return normalizeStoredDisplayMode(store.getItem(DISPLAY_MODE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * 持久化模式偏好并返回是否成功。
 * embed 属宿主临时态：清除存储而非写入，避免污染独立访问时的默认模式。
 */
export function persistDisplayMode(
  mode: DisplayMode,
  storage?: DisplayModeStorage | null,
): boolean {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return false;
  try {
    if (mode === "embed") {
      store.removeItem(DISPLAY_MODE_STORAGE_KEY);
    } else {
      store.setItem(DISPLAY_MODE_STORAGE_KEY, mode);
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * 需要能力授权的模式 → 能力码（与后端 require_module 门控一一对应）。
 * 所有进入入口（顶栏、URL、存储恢复、window 钩子）必须经同一张表判定。
 */
const MODE_CAPABILITY_CODES: Readonly<Partial<Record<DisplayMode, string>>> =
  Object.freeze({
    workspace: "workspace",
    diagnosis: "diagnosis",
    order: "medical_order",
    teach: "teach",
  });

/** 模式所需能力码；无门槛返回 null */
export function requiredCapabilityForMode(mode: DisplayMode): string | null {
  return MODE_CAPABILITY_CODES[mode] ?? null;
}

/**
 * 能力门控后的生效模式（纯函数）：无门槛或已开通 → 原模式；否则回退 full。
 * 调用方须保证 has 基于已加载的权益目录（未加载应视为未开通，默认拒绝）。
 */
export function resolveEntitledDisplayMode(
  mode: DisplayMode,
  has: (capabilityCode: string) => boolean,
): DisplayMode {
  const code = requiredCapabilityForMode(mode);
  return !code || has(code) ? mode : "full";
}

/** 初始模式解析的输入来源（全部由调用方采集，本模块不直接读 window） */
export interface DisplayModeSources {
  /** URL ?mode= 原始值 */
  urlMode?: unknown;
  /** 嵌入壳是否激活（embed=1 或 Tauri 运行时） */
  embedShellActive?: boolean;
  /** 本地存储原始值 */
  storedMode?: unknown;
}

/**
 * 按固定优先级解析初始显示模式：
 * 显式 URL ?mode= > 嵌入壳激活 > 存储偏好 > 默认 full。
 */
export function resolveInitialDisplayMode(
  sources: DisplayModeSources = {},
): DisplayMode {
  const fromUrl = normalizeDisplayModeParam(sources.urlMode);
  if (fromUrl) return fromUrl;
  if (sources.embedShellActive) return "embed";
  return normalizeStoredDisplayMode(sources.storedMode) ?? "full";
}
